import { Grid, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import {
  addStickyNote,
  auditColumn,
  CollectionTable,
  Column,
  deleteColumn,
  Expansion,
  ExpansionDetails,
  ExpansionSummary,
  FilterControl,
  isAuditReviewWorkflow,
  isReviewWorkflow,
  lineNumberColumn,
  noteColumn,
  onEditAction,
  orange,
  removeStickyNote,
  Search,
  shimColumn,
  truncateSpan,
  UndoRedo,
} from '../../core';
import { NO_NOTE_CONTEXT } from '../common/notes';
import { ReasonForVisitApi } from './reason-for-visit-api';

const rowStyle: any = {
  display: 'flex',
  flexDirection: 'row',
  gap: 24,
  alignItems: 'center',
  flexWrap: 'nowrap',
};

function Header({ left, right }) {
  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={2}
      style={{ width: '100%' }}
    >
      <Grid item>{left}</Grid>
      <Grid item style={{ flexGrow: 1 }} />
      <Grid item>{right}</Grid>
    </Grid>
  );
}

export function ReasonForVisitCollection(api: ReasonForVisitApi) {
  const { icds, notesApi, context, moveIcd, inactiveIcds } = api;
  const { auditICD } = context.config;

  const openNotes = notesApi.useNotes((s) => s.openDialog);
  const values = icds.data((s) => s.values);
  const isReview = isAuditReviewWorkflow();

  const actions = isReview
    ? [
        auditColumn(
          onEditAction(
            'Edit ICD Audit',
            () => icds.actions.refresh(),
            false,
            auditICD
          )
        ),
      ]
    : [];

  actions.push(
    noteColumn((e) =>
      openNotes(
        NO_NOTE_CONTEXT,
        e.value.note,
        async (note) => {
          await addStickyNote(
            { code: e.value.diagnosisCode, note: note.note },
            e.value
          );
          icds.actions.refresh();
        },
        () => {
          removeStickyNote(e.value);
        }
      )
    ),
    deleteColumn(
      (e) => {
        api.removeIcd(e.value);
      },
      (v) => v?.active,
      '',
      (e) => 'icd-delete-btn-' + e.row
    ),
    shimColumn()
  );

  const columns: Column[] = [
    lineNumberColumn(),
    {
      name: 'diagnosisCode',
      label: 'Code',
      style: { minWidth: '80px' },
      component: (e) => truncateSpan(e.value.diagnosisCode, '80px'),
    },
    {
      name: 'dxDescription',
      style: { width: '50vw', maxWidth: '60vw' },
      label: 'Description',
    },
    ...actions,
  ];

  const [expanded, setExpanded] = useState(values.length > 0);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0);
    }
  });

  const onClicked = () => {
    setExpanded(!expanded);
    setClicked(true);
  };

  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMore />}>
        <Header
          left={
            <div style={rowStyle}>
              <Typography variant="subtitle2">{`REASON FOR VISIT ${
                values.length ? '- ' + values.length : ''
              }`}</Typography>
            </div>
          }
          right={
            <div style={rowStyle}>
              <div onClick={(e) => e.stopPropagation()}>
                <UndoRedo api={api.icdChanges} htmlColor={orange} label='reason-for-visit' />
              </div>
              <FilterControl api={icds} />
            </div>
          }
        />
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid
          container
          direction="column"
          style={{ width: '100%', flexWrap: 'nowrap' }}
        >
          <CollectionTable
            readonly={api.context.readonly}
            onMove={moveIcd}
            noEdit={true}
            draggable={true}
            columns={columns}
            api={icds}
            containerProps={{ elevation: 0 }}
            name="reason-for-visit-table"
            label={`REASON FOR VISIT ${values.length ? '- ' + values.length : ''}`}
          />
          {isReview ? (
            <CollectionTable
              readonly={api.context.readonly}
              headless={true}
              noEdit={true}
              draggable={false}
              columns={columns}
              api={inactiveIcds}
              containerProps={{ elevation: 0 }}
              name='reason-for-visit-inactive-table'
              label={`INACTIVE REASON FOR VISIT ${values.length ? '- ' + values.length : ''}`}
            />
          ) : null}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}
